import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Truck, CheckCircle2, Clock, Bell, ShieldAlert, X, Heart, Share2 } from "lucide-react";
import { api, rupee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

const STEPS = ["Placed", "Confirmed", "In Progress", "Shipped", "Delivered"];
const ICONS = { Placed: Clock, Confirmed: CheckCircle2, "In Progress": Package, Shipped: Truck, Delivered: CheckCircle2 };

function Timeline({ status, order }) {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex items-center mt-4">
      {STEPS.map((s, i) => {
        const done = i <= idx;
        const Ic = ICONS[s];
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full grid place-items-center ${done ? "bg-clay text-sand" : "bg-surface text-mute border border-line"}`}>
                <Ic className="w-4 h-4" />
              </div>
              <span className={`text-[10px] mt-1.5 ${done ? "text-ink" : "text-mute"} whitespace-nowrap`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${i < idx ? "bg-clay" : "bg-line"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function BuyerAccount() {
  const { user } = useAuth();
  const { count: wishCount } = useWishlist();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [saved, setSaved] = useState([]);
  const [tab, setTab] = useState(params.get("tab") === "saved" ? "saved" : "orders");
  const [issueFor, setIssueFor] = useState(null);
  const [issue, setIssue] = useState({ kind: "refund", reason: "", details: "" });

  const loadDisputes = () => api.get("/disputes").then(({ data }) => setDisputes(data));
  const loadSaved = () => api.get("/wishlist").then(({ data }) => setSaved(data.products || []));

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data));
    api.get("/notifications").then(({ data }) => setNotifs(data));
    loadDisputes();
    loadSaved();
    api.post("/notifications/read").catch(() => {});
  }, []);

  useEffect(() => { if (tab === "saved") loadSaved(); }, [tab, wishCount]);

  const disputedOrders = new Set(disputes.filter((d) => !["Resolved", "Rejected", "Refunded"].includes(d.status)).map((d) => d.order_id));

  const submitIssue = async () => {
    if (!issue.reason.trim() || !issue.details.trim()) { toast.error("Please add a reason and details"); return; }
    try {
      await api.post("/disputes", { order_id: issueFor.id, ...issue });
      toast.success("Issue raised — the seller has been notified");
      setIssueFor(null); setIssue({ kind: "refund", reason: "", details: "" });
      loadDisputes(); setTab("disputes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not raise issue");
    }
  };

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
        <div className="overline text-clay mb-3">My account</div>
        <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">Hello, {user?.name?.split(" ")[0]}.</h1>

        <div className="flex gap-2 mt-8 border-b border-line">
          {[["orders", `My Orders (${orders.length})`], ["saved", `Saved (${saved.length})`], ["disputes", `Issues (${disputes.length})`], ["notifs", `Notifications (${notifs.length})`]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className={`px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === v ? "border-clay text-clay" : "border-transparent text-mute"}`}
              data-testid={`tab-${v}`}>{l}</button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-8 space-y-6">
            {orders.length === 0 ? (
              <div className="py-16 text-center text-mute">No orders yet. <Link to="/shop" className="text-clay link-underline">Start shopping</Link></div>
            ) : orders.map((o) => (
              <div key={o.id} className="bg-surface rounded-sm border border-line p-5 sm:p-6" data-testid={`order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="overline text-mute">Order #{o.id.slice(-6)} · {o.seller_name}</div>
                    <div className="text-sm text-mute mt-0.5">{o.payment_status} · {new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                  </div>
                  <span className="text-sm px-3 py-1 rounded-full bg-clay/10 text-clay">{o.status}</span>
                </div>

                <div className="flex flex-wrap gap-4 mt-4">
                  {o.items.map((it) => (
                    <Link key={it.product_id} to={`/product/${it.product_id}`} className="flex gap-3 items-center">
                      <img src={it.image} alt="" className="w-14 h-16 rounded-sm object-cover" />
                      <div className="text-sm">
                        <div className="leading-tight">{it.title}</div>
                        <div className="text-mute text-xs">Qty {it.qty} · {rupee(it.price)}</div>
                      </div>
                    </Link>
                  ))}
                </div>

                <Timeline status={o.status} order={o} />

                {o.status === "Shipped" && o.tracking_number && (
                  <div className="mt-4 text-sm bg-sand rounded-sm px-4 py-3 border border-line">
                    <Truck className="w-4 h-4 inline text-clay mr-2" />
                    Shipped via <b>{o.courier}</b> · Tracking: <b>{o.tracking_number}</b>
                  </div>
                )}

                <div className="flex flex-wrap justify-between items-center gap-3 mt-4 pt-4 border-t border-line">
                  <span className="text-sm text-mute">Ships to {o.address?.city}, {o.address?.state}</span>
                  <div className="flex items-center gap-4">
                    {!disputedOrders.has(o.id) && o.status !== "Refunded" && (
                      <button onClick={() => setIssueFor(o)} className="flex items-center gap-1.5 text-sm text-clay hover:underline" data-testid={`raise-issue-${o.id}`}>
                        <ShieldAlert className="w-4 h-4" /> Raise an issue
                      </button>
                    )}
                    <span className="font-semibold" title={o.shipping ? `Incl. ${rupee(o.shipping)} shipping` : "Free shipping"}>{rupee(o.total)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "disputes" && (
          <div className="mt-8 space-y-4">
            {disputes.length === 0 ? (
              <div className="py-16 text-center text-mute">No issues raised. If a gift arrives damaged or wrong, you can raise an issue from any order.</div>
            ) : disputes.map((d) => (
              <div key={d.id} className="bg-surface rounded-sm border border-line p-5" data-testid={`dispute-${d.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="overline text-mute">Order #{d.order_short} · {d.seller_name}</div>
                    <div className="text-sm mt-0.5 capitalize">{d.kind.replace(/_/g, " ")} · {d.reason}</div>
                  </div>
                  <span className={`text-sm px-3 py-1 rounded-full ${d.status === "Open" ? "bg-terra/15 text-terra" : d.status === "Refunded" ? "bg-forest/15 text-forest" : "bg-clay/10 text-clay"}`}>{d.status}</span>
                </div>
                <p className="text-sm text-mute mt-3">{d.details}</p>
                {d.seller_message && (
                  <div className="mt-3 bg-sand rounded-sm px-4 py-3 border border-line text-sm">
                    <span className="overline text-clay">Seller response</span>
                    <p className="mt-1">{d.seller_message}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "saved" && (
          <div className="mt-8">
            {saved.length === 0 ? (
              <div className="py-16 text-center text-mute">
                <Heart className="w-8 h-8 mx-auto text-line mb-3" />
                No favourites yet. Tap the heart on any gift to save it here. <Link to="/shop" className="text-clay link-underline">Browse gifts</Link>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-mute">{saved.length} saved {saved.length === 1 ? "gift" : "gifts"}</p>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/wishlist/${user.id}`;
                      navigator.clipboard?.writeText(url).then(
                        () => toast.success("Wishlist link copied — share it as a gift hint!"),
                        () => toast.success(url)
                      );
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-clay text-sand px-5 py-2.5 text-sm hover:bg-forest transition-colors"
                    data-testid="share-wishlist-btn"
                  >
                    <Share2 className="w-4 h-4" /> Share my wishlist
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8" data-testid="saved-grid">
                  {saved.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "notifs" && (
          <div className="mt-8 space-y-3">
            {notifs.length === 0 ? (
              <div className="py-16 text-center text-mute">No notifications yet.</div>
            ) : notifs.map((n) => (
              <div key={n.id} className="flex items-start gap-3 bg-surface rounded-sm border border-line px-4 py-3" data-testid={`notif-${n.id}`}>
                <Bell className="w-4 h-4 text-clay mt-0.5" />
                <div>
                  <p className="text-sm">{n.message}</p>
                  <p className="text-xs text-mute mt-0.5">{new Date(n.created_at).toLocaleString("en-IN")}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RAISE ISSUE MODAL */}
      <AnimatePresence>
        {issueFor && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div className="absolute inset-0 bg-ink/50" onClick={() => setIssueFor(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
              className="relative bg-sand w-full max-w-md mx-5 rounded-sm p-6 sm:p-8" data-testid="issue-modal">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-serif text-2xl">Raise an issue</h2>
                <button onClick={() => setIssueFor(null)}><X className="w-5 h-5" /></button>
              </div>
              <p className="text-mute text-sm mb-5">Order #{issueFor.id.slice(-6)} · {issueFor.seller_name}</p>
              <label className="overline text-mute block mb-2">What went wrong?</label>
              <div className="flex flex-wrap gap-2 mb-4">
                {[["refund", "Refund request"], ["damage", "Arrived damaged"], ["not_as_described", "Not as described"], ["other", "Other"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setIssue({ ...issue, kind: v })}
                    className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${issue.kind === v ? "bg-clay text-sand border-clay" : "border-line text-ink/70"}`}
                    data-testid={`issue-kind-${v}`}>{l}</button>
                ))}
              </div>
              <label className="overline text-mute block mb-1.5">Short reason</label>
              <input value={issue.reason} onChange={(e) => setIssue({ ...issue, reason: e.target.value })} placeholder="e.g. Broken in transit"
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay mb-4" data-testid="issue-reason" />
              <label className="overline text-mute block mb-1.5">Details</label>
              <textarea value={issue.details} onChange={(e) => setIssue({ ...issue, details: e.target.value })} rows={3} placeholder="Tell the seller what happened…"
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay resize-none" data-testid="issue-details" />
              <button onClick={submitIssue} className="w-full rounded-full bg-clay text-sand py-3.5 mt-4 text-sm hover:bg-forest transition-colors" data-testid="issue-submit">Submit to seller</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
